"use client";

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2, PlayCircle } from "lucide-react";
import {
  VideoPlayer,
  VideoPlayerControlBar,
  VideoPlayerFullscreenButton,
  VideoPlayerMuteButton,
  VideoPlayerPlayButton,
  VideoPlayerSeekBackwardButton,
  VideoPlayerSeekForwardButton,
  VideoPlayerTimeDisplay,
  VideoPlayerTimeRange,
  VideoPlayerVolumeRange,
} from "@/components/kibo-ui/video-player";
import { VideoPlayerSettings } from "@/components/common/VideoPlayerSettings";
import { cn } from "@/lib/utils";
import { getPublitioPlayerPublic } from "@/actions/publitio-player-public";
import { getPublitioPlayer } from "@/actions/publitio-player";
import {
  trackVideoCompleted,
  trackVideoProgress,
  trackVideoStarted,
} from "@/lib/analytics/track-client";
import type {
  BaseVideoProps,
  VideoContextType,
  VideoProgressProps,
} from "@/lib/analytics/types";
import {
  parsePublitioSourceHtml,
  type PublitioPlayerSource,
} from "./publitio-player-source";

type QualityLevel = {
  height: number;
  bitrate: number;
  index: number;
};

type MasterclassVideoPlayerProps = {
  publitioMediaId: string;
  playerId?: string;
  access: "public" | "authenticated";
  videoContextType: Extract<
    VideoContextType,
    | "masterclass_landing"
    | "masterclass_full"
    | "platform_vsl"
    | "masterclass_offer_vsl"
  >;
  videoContextId: number;
  videoContextName: string;
  videoAssetKey: string;
  videoVariantKey: string;
  className?: string;
};

const MILESTONES: VideoProgressProps["progress_pct"][] = [25, 50, 75, 95];

function buildVideoProps({
  videoAssetKey,
  videoContextId,
  videoContextName,
  videoContextType,
  videoVariantKey,
}: Omit<MasterclassVideoPlayerProps, "access" | "className" | "playerId" | "publitioMediaId">): BaseVideoProps {
  return {
    video_id: null,
    video_provider: "publitio",
    video_context_type: videoContextType,
    video_context_id: videoContextId,
    video_context_name: videoContextName,
    course_id: null,
    course_name: null,
    lesson_id: null,
    lesson_name: null,
    program_id: null,
    program_name: null,
    video_asset_key: videoAssetKey,
    video_variant_key: videoVariantKey,
  };
}

function ControlledMasterclassVideoPlayer({
  className,
  onError,
  poster,
  src,
  videoProps,
}: {
  className?: string;
  onError: (message: string) => void;
  poster?: string;
  src: string;
  videoProps: BaseVideoProps;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<import("hls.js").default | null>(null);
  const bufferingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const trackedMilestones = useRef(new Set<VideoProgressProps["progress_pct"]>());
  const hasTrackedStart = useRef(false);
  const hasTrackedCompletion = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const [showBuffering, setShowBuffering] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [qualityLevels, setQualityLevels] = useState<QualityLevel[]>([]);
  const [currentQuality, setCurrentQuality] = useState(-1);

  const isHlsSource = useMemo(
    () => src.toLowerCase().includes(".m3u8"),
    [src],
  );
  const isLoading = initialLoading || showBuffering;

  const handleEnded = useEffectEvent(() => {
    if (hasTrackedCompletion.current) return;
    hasTrackedCompletion.current = true;
    trackVideoCompleted(videoProps);
  });

  const handlePlay = useEffectEvent(() => {
    if (hasTrackedStart.current) return;
    hasTrackedStart.current = true;
    trackVideoStarted(videoProps);
  });

  const handleTimeUpdate = useEffectEvent(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;

    const progressPct = (video.currentTime / video.duration) * 100;

    for (const milestone of MILESTONES) {
      if (
        progressPct >= milestone &&
        !trackedMilestones.current.has(milestone)
      ) {
        trackedMilestones.current.add(milestone);
        trackVideoProgress({
          ...videoProps,
          progress_pct: milestone,
        });
      }
    }
  });

  const handlePlaybackRateChange = useCallback((rate: number) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  }, []);

  const handleQualityChange = useCallback((index: number) => {
    setCurrentQuality(index);
    if (hlsRef.current) {
      hlsRef.current.currentLevel = index;
    }
  }, []);

  useEffect(() => {
    if (buffering) {
      bufferingTimeoutRef.current = setTimeout(() => {
        setShowBuffering(true);
      }, 200);
    } else {
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }
      setShowBuffering(false);
    }

    return () => {
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
      }
    };
  }, [buffering]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let nativeMetadataHandler: (() => void) | null = null;
    let nativeErrorHandler: (() => void) | null = null;

    const loadVideo = async () => {
      setInitialLoading(true);
      setBuffering(false);
      setQualityLevels([]);
      setCurrentQuality(-1);
      onError("");

      if (!isHlsSource) {
        video.src = src;
        video.load();
        nativeMetadataHandler = () => setInitialLoading(false);
        nativeErrorHandler = () => {
          setInitialLoading(false);
          onError("Failed to load video");
        };
        video.addEventListener("loadedmetadata", nativeMetadataHandler);
        video.addEventListener("error", nativeErrorHandler);
        return;
      }

      try {
        const Hls = (await import("hls.js")).default;

        if (Hls.isSupported()) {
          if (hlsRef.current) {
            hlsRef.current.destroy();
          }

          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 90,
          });

          hlsRef.current = hls;
          hls.loadSource(src);
          hls.attachMedia(video);

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setInitialLoading(false);

            const levels = hls.levels.map(
              (level: { height: number; bitrate: number }, index: number) => ({
                height: level.height || 0,
                bitrate: level.bitrate || 0,
                index,
              }),
            );

            if (levels.length > 1) {
              setQualityLevels(levels);
            }
          });

          hls.on(Hls.Events.LEVEL_SWITCHING, () => {
            setBuffering(true);
          });

          hls.on(Hls.Events.LEVEL_SWITCHED, () => {
            setBuffering(false);
          });

          hls.on(Hls.Events.ERROR, (_, data) => {
            if (!data.fatal) return;

            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                onError("Network error - please check your connection");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                onError("Media error - attempting recovery");
                hls.recoverMediaError();
                break;
              default:
                onError("Failed to load video");
                hls.destroy();
                break;
            }
            setInitialLoading(false);
            setBuffering(false);
          });

          return;
        }

        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = src;
          video.load();
          nativeMetadataHandler = () => setInitialLoading(false);
          nativeErrorHandler = () => {
            setInitialLoading(false);
            onError("Failed to load video");
          };
          video.addEventListener("loadedmetadata", nativeMetadataHandler);
          video.addEventListener("error", nativeErrorHandler);
          return;
        }

        setInitialLoading(false);
        onError("HLS is not supported in this browser");
      } catch (error) {
        setInitialLoading(false);
        onError(
          error instanceof Error ? error.message : "Failed to initialize player",
        );
      }
    };

    void loadVideo();

    return () => {
      if (nativeMetadataHandler) {
        video.removeEventListener("loadedmetadata", nativeMetadataHandler);
      }
      if (nativeErrorHandler) {
        video.removeEventListener("error", nativeErrorHandler);
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isHlsSource, onError, src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleWaiting = () => setBuffering(true);
    const handlePlaying = () => setBuffering(false);
    const handleCanPlay = () => setBuffering(false);
    const handleSeeking = () => setBuffering(true);
    const handleSeeked = () => setBuffering(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("seeked", handleSeeked);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("seeked", handleSeeked);
    };
  }, []);

  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden rounded-lg bg-black",
        className,
      )}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
          <Loader2 size={48} className="text-brand-red animate-spin" />
        </div>
      )}

      <VideoPlayer className="h-full w-full">
        <video
          ref={videoRef}
          slot="media"
          className="h-full w-full"
          playsInline
          poster={poster}
          preload="metadata"
          tabIndex={-1}
          suppressHydrationWarning
        />
        <VideoPlayerControlBar>
          <VideoPlayerPlayButton />
          <VideoPlayerSeekBackwardButton className="hidden sm:inline-flex" />
          <VideoPlayerSeekForwardButton className="hidden sm:inline-flex" />
          <VideoPlayerTimeDisplay showDuration />
          <VideoPlayerTimeRange />
          <VideoPlayerMuteButton />
          <VideoPlayerVolumeRange className="hidden sm:inline-flex" />
          <VideoPlayerSettings
            playbackRate={playbackRate}
            onPlaybackRateChange={handlePlaybackRateChange}
            qualityLevels={qualityLevels}
            currentQuality={currentQuality}
            onQualityChange={handleQualityChange}
          />
          <VideoPlayerFullscreenButton />
        </VideoPlayerControlBar>
      </VideoPlayer>
    </div>
  );
}

export function MasterclassVideoPlayer({
  access,
  className,
  playerId = "11848",
  publitioMediaId,
  videoAssetKey,
  videoContextId,
  videoContextName,
  videoContextType,
  videoVariantKey,
}: MasterclassVideoPlayerProps) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<PublitioPlayerSource | null>(null);
  const [fallbackIframeHtml, setFallbackIframeHtml] = useState("");

  const videoProps = useMemo(
    () =>
      buildVideoProps({
        videoAssetKey,
        videoContextId,
        videoContextName,
        videoContextType,
        videoVariantKey,
      }),
    [
      videoAssetKey,
      videoContextId,
      videoContextName,
      videoContextType,
      videoVariantKey,
    ],
  );

  useEffect(() => {
    const loadPlayer = async () => {
      setLoading(true);
      setError("");
      setSource(null);
      setFallbackIframeHtml("");

      try {
        const result =
          access === "public"
            ? await getPublitioPlayerPublic(publitioMediaId, playerId)
            : await getPublitioPlayer(publitioMediaId, playerId);

        if ("redirectTo" in result && result.redirectTo) {
          window.location.href = result.redirectTo;
          return;
        }

        if (!result.success || !result.source_html) {
          setError(result.error ?? "Error al cargar el video");
          return;
        }

        const parsedSource = parsePublitioSourceHtml(result.source_html);

        if (parsedSource?.src.toLowerCase().includes(".m3u8") && result.iframe_html) {
          console.info("[MasterclassVideoPlayer] Using Publitio iframe for HLS asset", {
            access,
            publitioMediaId,
          });
          setFallbackIframeHtml(result.iframe_html);
          return;
        }

        if (!parsedSource) {
          if (result.iframe_html) {
            console.warn(
              "[MasterclassVideoPlayer] Falling back to iframe player",
              {
                access,
                publitioMediaId,
              },
            );
            setFallbackIframeHtml(result.iframe_html);
            return;
          }

          setError("No se pudo preparar el video para tracking");
          return;
        }

        setSource(parsedSource);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error desconocido");
      } finally {
        setLoading(false);
      }
    };

    void loadPlayer();
  }, [access, playerId, publitioMediaId]);

  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden rounded-lg bg-black",
        className,
      )}
    >
      {loading ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <Loader2 className="text-forge-red h-12 w-12 animate-spin" />
          <p className="font-mono text-sm uppercase tracking-wider text-white/60">
            Cargando video...
          </p>
        </div>
      ) : error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <PlayCircle className="h-12 w-12 text-red-400" />
          <p className="text-lg font-semibold text-red-400">
            Error al cargar el video
          </p>
          <p className="text-sm text-white/60">{error}</p>
        </div>
      ) : source ? (
        <ControlledMasterclassVideoPlayer
          onError={setError}
          poster={source.poster}
          src={source.src}
          videoProps={videoProps}
        />
      ) : fallbackIframeHtml ? (
        <div
          className="absolute inset-0 [&_iframe]:h-full [&_iframe]:w-full"
          dangerouslySetInnerHTML={{ __html: fallbackIframeHtml }}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <PlayCircle className="h-12 w-12 text-red-400" />
          <p className="text-lg font-semibold text-red-400">
            Error al cargar el video
          </p>
          <p className="text-sm text-white/60">No se pudo preparar el video</p>
        </div>
      )}
    </div>
  );
}
